import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import { userInfo } from '../../../store/selectors/user';
import { impactOccuer } from '../../../utils/hapticEvents';

type BannerLanguage = 'ru' | 'uz' | 'en';

type ProfileBanner = {
  id: string;
  /** Готовая картинка с текстом — своя на каждый язык. */
  images: Record<BannerLanguage, string>;
  /** Текст картинки (заголовок и подпись) — для экранного диктора. */
  labelKeys: string[];
  onAction: () => void;
};

const toBannerLanguage = (language: string): BannerLanguage =>
  language === 'uz' || language === 'en' ? language : 'ru';

/**
 * Баннеры под именем в профиле: карточка 343×106 (картинки 4x, 1372×424),
 * скругление уже нарисовано в самой картинке.
 *
 * Лентой с горизонтальной прокруткой: в макете справа выглядывает следующий
 * баннер. Пока баннер один — он во всю ширину, без «хвоста». Нечего
 * показывать — блока нет вовсе, без пустого места.
 */
export const ProfileBanners = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector(userInfo);
  const language = toBannerLanguage(i18n.language);

  // Баннер про бонусы на день рождения: нужна только дата рождения (её
  // задают на экране «Личные данные»). Указана — баннера нет.
  const hasBirthDate = Boolean(user.dob);

  const banners: ProfileBanner[] = [];
  if (!hasBirthDate) {
    banners.push({
      id: 'birthday',
      images: {
        ru: '/background/birthday-banner-ru.webp',
        uz: '/background/birthday-banner-uz.webp',
        en: '/background/birthday-banner-en.webp',
      },
      labelKeys: ['birthday-banner', 'birthday-banner-description'],
      onAction: () => navigate('/profile/user-info'),
    });
  }

  if (!banners.length) return null;

  const isSingle = banners.length === 1;

  return (
    // Ровно по ширине колонки профиля: вылет за её края (-mx-4) прокрутил
    // бы вбок всю страницу — колонка лежит в блоке с overflow-y-auto.
    <div className="flex w-full snap-x snap-mandatory gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {banners.map(banner => (
        // Кнопки на картинке нет — нажимается вся карточка.
        <button
          key={banner.id}
          type="button"
          aria-label={banner.labelKeys.map(key => t(key)).join(' ')}
          onClick={() => {
            impactOccuer('light');
            banner.onAction();
          }}
          className={cn(
            'block shrink-0 snap-start overflow-hidden rounded-2xl',
            isSingle ? 'w-full' : 'w-[calc(100%-24px)] max-w-[343px]'
          )}
        >
          <img
            src={banner.images[language]}
            alt=""
            className="block aspect-[1372/424] h-auto w-full"
            draggable={false}
          />
        </button>
      ))}
    </div>
  );
};
